import { ToastService } from '@/app/core/services/toast.service';
import { CropPhenologyProxy, GroupedPlantationViewModel, PhenologicalStageItemViewModel, UpsertRotationPhenologicalStageCommand } from '@/app/proxy/proxies';
import { SharedModule } from '@/app/shared/shared.module';
import { Component, inject, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DateTime } from 'luxon';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { finalize } from 'rxjs';
import { MixpanelTrackerService } from '@/app/core/analytics';
import { PlantationService } from '@/app/core/services/plantation.service';

@Component({
  selector: 'app-add-next-stage-modal',
  imports: [SharedModule],
  templateUrl: './add-next-stage-modal.component.html',
  styleUrl: './add-next-stage-modal.component.less',
  providers: [CropPhenologyProxy]
})
export class AddNextStageModalComponent implements OnInit {
  private _fb = inject(FormBuilder);
  private _cropPhenologyProxy = inject(CropPhenologyProxy);
  private _toastService = inject(ToastService);
  private _dialogRef = inject(DynamicDialogRef);
  private _mixpanelTracker = inject(MixpanelTrackerService);
  private _plantationService = inject(PlantationService);
  form: FormGroup;

  @Input() mode: 'addNext' | 'update' = 'addNext';
  @Input() selectedPlantation: GroupedPlantationViewModel;
  @Input() harvestEndDate: DateTime<boolean>;
  @Input() sowingStartDate: DateTime<boolean>;

  @Input() currentStage: PhenologicalStageItemViewModel;
  @Input() nextStage: PhenologicalStageItemViewModel;

  @Input() stageIndex: number;
  @Input() stages: PhenologicalStageItemViewModel[] = [];

  loading = false;
  minDate: Date;
  maxDate: Date;

  ngOnInit(): void {
    this.form = this._fb.group({
      stageId: [null],
      stageName: [{ value: null, disabled: true }],
      stageDate: [null, [Validators.required]],
    });

    let stage: PhenologicalStageItemViewModel;
    let stageDate: Date | null | undefined;
    if (this.mode === 'update') {
      stage = this.stages[this.stageIndex];
      stageDate = stage?.stageDate?.startOf('day').toJSDate();

      if (this.stageIndex === 0) {
        this.minDate = this.sowingStartDate?.startOf('day').toJSDate();
        this.maxDate = (this.stages[this.stageIndex + 1]?.stageDate ?? this.harvestEndDate)?.startOf('day').toJSDate();
      } else if (this.stageIndex > 0) {
        this.minDate = this.stages[this.stageIndex - 1]?.stageDate?.plus({ days: 1 }).startOf('day').toJSDate();
        this.maxDate = (this.stages[this.stageIndex + 1]?.stageDate?.minus({ days: 1 }) ?? this.harvestEndDate)?.startOf('day').toJSDate();
      }
    } else {
      stage = this.nextStage;
      this.minDate = (this.currentStage?.stageDate?.plus({ days: 1 }) ?? this.sowingStartDate)?.startOf('day').toJSDate();
      stageDate = null;
    }

    if (stage) {
      this.form.get('stageId').setValue(stage.stageId);
      this.form.get('stageName').setValue(stage.stageName);
      this.form.get('stageDate').setValue(stageDate);
    }
  }

  submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      console.log('this.form :>> ', this.form);
      return;
    }

    const formValue = this.form.value;
    const payload = new UpsertRotationPhenologicalStageCommand();
    payload.rotationId = this.selectedPlantation.activeRotationId;
    payload.stageId = formValue.stageId;
    
    const selectedDate = formValue.stageDate as Date;
    const local = DateTime.fromJSDate(selectedDate, { zone: 'local' });
    payload.stageDate = DateTime.fromObject(
      { year: local.year, month: local.month, day: local.day },
      { zone: 'utc' }
    ).startOf('day');
    if (!payload.stageDate?.isValid) {
      this._toastService.error();
      return;
    }

    this.loading = true;
    this._cropPhenologyProxy.cropPhenology_UpsertRotationPhenologicalStage(payload)
      .pipe(
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: res => {
          if (res.isSuccess) {
            this._toastService.success();
            if (this.mode === 'addNext' && payload.stageDate) {
              this.trackServicesPhenologicalStageAdded(payload.stageDate);
            }
            if (this.mode === 'update' && payload.stageDate) {
              this.trackServicesPhenologicalStageEdited(payload.stageDate);
            }
            this._dialogRef.close(true);
          }
        }
      });
  }

  private trackServicesPhenologicalStageAdded(nextStageDate: DateTime): void {
    const land = this._plantationService.selectedLand;
    const enterpriseId = land?.enterpriseId || this._plantationService.enterpriseId;
    const enterprise = this._plantationService.originalResult?.find((item) => item.id === enterpriseId);

    this._mixpanelTracker.trackServicesPhenologicalStageAdded({
      year: nextStageDate.year,
      crop: this.selectedPlantation?.crop?.value ?? '-',
      'current stage': this.currentStage?.stageName ?? '-',
      'current stage date': this.currentStage?.stageDate?.toISODate() ?? undefined,
      'next stage': this.nextStage?.stageName ?? this.form.get('stageName')?.value ?? '-',
      'next stage date': nextStageDate.toISODate(),
      'plantation id': this.selectedPlantation.id,
      'plantation name': this.selectedPlantation?.name,
      'land id': land?.id,
      'land name': land?.name,
      'enterprise id': enterpriseId,
      'enterprise name': enterprise?.enterpriseName,
    });
  }

  private trackServicesPhenologicalStageEdited(newStageDate: DateTime): void {
    const editedStage = this.stages[this.stageIndex];
    if (!editedStage) {
      return;
    }

    const land = this._plantationService.selectedLand;
    const enterpriseId = land?.enterpriseId || this._plantationService.enterpriseId;
    const enterprise = this._plantationService.originalResult?.find((item) => item.id === enterpriseId);

    this._mixpanelTracker.trackServicesPhenologicalStageEdited({
      year: newStageDate.year,
      crop: this.selectedPlantation?.crop?.value ?? '-',
      stage: editedStage.stageName ?? '-',
      'old stage date': editedStage.stageDate?.toISODate(),
      'new stage date': newStageDate.toISODate(),
      'plantation id': this.selectedPlantation.id,
      'plantation name': this.selectedPlantation?.name,
      'land id': land?.id,
      'land name': land?.name,
      'enterprise id': enterpriseId,
      'enterprise name': enterprise?.enterpriseName,
    });
  }

  cancel() {
    this._dialogRef.close();
  }
}
